import * as React from 'react'
import { TransitionProps } from '../transitions'
import { Modal, ModalProps } from '../Modal'
import { Paper, PaperProps } from '../Paper'
import { Slide, SlideProps, Direction as AnchorDirection } from '../Slide'
import { sx as sxFunc, SxProps } from '../sx'
import clsx from 'clsx'
import styles from './index.module.scss'
import { capitalize } from '../../utils'
import { Box, BoxProps } from '../Box'

export interface DrawerClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `variant="permanent or persistent"`. */
  docked: string
  /** Styles applied to the Paper component. */
  paper: string
  /** Styles applied to the Paper component if `anchor="left"`. */
  paperAnchorLeft: string
  /** Styles applied to the Paper component if `anchor="right"`. */
  paperAnchorRight: string
  /** Styles applied to the Paper component if `anchor="top"`. */
  paperAnchorTop: string
  /** Styles applied to the Paper component if `anchor="bottom"`. */
  paperAnchorBottom: string
  /** Styles applied to the Paper component if `anchor="left"` and `variant` is not "temporary". */
  paperAnchorDockedLeft: string
  /** Styles applied to the Paper component if `anchor="top"` and `variant` is not "temporary". */
  paperAnchorDockedTop: string
  /** Styles applied to the Paper component if `anchor="right"` and `variant` is not "temporary". */
  paperAnchorDockedRight: string
  /** Styles applied to the Paper component if `anchor="bottom"` and `variant` is not "temporary". */
  paperAnchorDockedBottom: string
  /** Styles applied to the Modal component. */
  modal: string
}

export interface DrawerProps extends Omit<ModalProps, 'open' | 'children'> {
  /**
   * Side from which the drawer will appear.
   * @default 'left'
   */
  anchor?: 'left' | 'right' | 'top' | 'bottom'
  /**
   * The content of the component.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<DrawerClasses>
  /**
   * The elevation of the drawer.
   * @default 1
   */
  elevation?: PaperProps['elevation']
  /**
   * Props applied to the [`Modal`](/material-ui/api/modal/) element.
   * @default {}
   */
  ModalProps?: Partial<ModalProps>
  /**
   * Callback fired when the component requests to be closed.
   * The `reason` parameter can optionally be used to control the response to `onClose`.
   *
   * @param {object} event The event source of the callback.
   * @param {string} reason Can be: `"escapeKeyDown"`, `"backdropClick"`.
   */
  onClose?: ModalProps['onClose']
  /**
   * If `true`, the component is shown.
   * @default false
   */
  open?: boolean
  /**
   * Props applied to the [`Paper`](/material-ui/api/paper/) element.
   * @default {}
   */
  PaperProps?: Partial<PaperProps>
  /**
   * Props applied to the [`Slide`](/material-ui/api/slide/) element.
   */
  SlideProps?: Partial<SlideProps>
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The duration for the transition, in milliseconds.
   * You may specify a single timeout for all transitions, or individually with an object.
   * @default {
   *   enter: theme.transitions.duration.enteringScreen,
   *   exit: theme.transitions.duration.leavingScreen,
   * }
   */
  transitionDuration?: TransitionProps['timeout']
  /**
   * The variant to use.
   * @default 'temporary'
   */
  variant?: 'permanent' | 'persistent' | 'temporary'
  /**
   *
   */
  BoxProps?: BoxProps
}

type Direction = 'ltr' | 'rtl'

const direction: Direction = 'ltr'

const oppositeDirection: Record<string, AnchorDirection> = {
  left: 'right',
  right: 'left',
  top: 'down',
  bottom: 'up',
}

export function isHorizontal(anchor: DrawerProps['anchor']) {
  return ['left', 'right'].indexOf(anchor!) !== -1
}

export function getAnchor(
  direction: Direction,
  anchor: DrawerProps['anchor']
): DrawerProps['anchor'] {
  return direction === 'rtl' && isHorizontal(anchor)
    ? (oppositeDirection[anchor as string] as DrawerProps['anchor'])
    : anchor
}

export const Drawer = React.forwardRef<HTMLDivElement, DrawerProps>(
  (props, ref) => {
    const defaultTransitionDuration = {
      enter: 255, // refer to css variable --transitionDurationEnteringScreen
      exit: 195, // refer to css variable --transitionDurationLeavingScreen
    }

    const {
      anchor: anchorProp = 'left',
      children,
      classes,
      className,
      elevation = 1,
      hideBackdrop = false,
      ModalProps = {},
      onClose,
      open = false,
      PaperProps = {},
      SlideProps,
      transitionDuration = defaultTransitionDuration,
      variant = 'temporary',
      sx,
      BoxProps,
      ...others
    } = props

    // Let's assume that the Drawer will always be rendered on user space.
    // We use this state is order to skip the appear transition during the
    // initial mount of the component.
    const mounted = React.useRef(false)
    React.useEffect(() => {
      mounted.current = true
    }, [])

    const anchorInvariant = getAnchor(direction, anchorProp)
    const anchor = anchorProp

    const drawer = (
      <Paper
        elevation={variant === 'temporary' ? elevation : 0}
        square
        {...PaperProps}
        className={clsx(
          styles.drawerPaper,
          classes?.paper,
          PaperProps.className,
          styles[`anchor-${anchor}`],
          // @ts-ignore
          classes?.[`paperAnchor${capitalize(anchor)}`],
          variant !== 'temporary' && [
            // @ts-ignore
            classes?.[`paperAnchorDocked${capitalize(anchor)}`],
            styles.anchorDocked,
          ]
        )}
      >
        {children}
      </Paper>
    )

    if (variant === 'permanent') {
      return (
        <Box
          Component="div"
          className={clsx(
            styles.dockedRoot,
            classes?.root,
            classes?.docked,
            className,
            sxFunc(sx),
            BoxProps?.className
          )}
          {...BoxProps}
        >
          {drawer}
        </Box>
      )
    }

    const slidingDrawer = (
      // @ts-ignore
      <Slide
        in={open}
        direction={oppositeDirection[anchorInvariant!]}
        timeout={transitionDuration}
        appear={mounted.current}
        {...SlideProps}
      >
        {drawer}
      </Slide>
    )

    if (variant === 'persistent') {
      return (
        <Box
          Component="div"
          className={clsx(
            styles.dockedRoot,
            classes?.root,
            classes?.docked,
            className,
            sxFunc(sx),
            BoxProps?.className
          )}
          {...BoxProps}
        >
          {slidingDrawer}
        </Box>
      )
    }

    // variant = temperoary
    return (
      <Modal
        open={open}
        onClose={onClose}
        hideBackdrop={hideBackdrop}
        ref={ref}
        className={clsx(
          classes?.root,
          classes?.modal,
          styles.root,
          className,
          sxFunc(sx)
        )}
        {...others}
        {...ModalProps}
      >
        {slidingDrawer}
      </Modal>
    )
  }
)

Drawer.displayName = 'Drawer'
